import type {
  Certification,
  UHMember,
  UhmRole,
  UserHouseholdData,
} from 'src/types/Certification';

/**
 * Human-readable labels for UserHouseholdMember roles. These are the UHM
 * (profile) roles, not cert member types — the values map onto the cert
 * member type names that getRoleColors() understands so profile cards are
 * colour-coded consistently with the cert composition flow.
 */
export const UHM_ROLE_LABELS: Record<UhmRole, string> = {
  hoh: 'Head of Household',
  co_hoh: 'Co-Head of Household',
  spouse: 'Spouse',
  member: 'Household Member',
  helper: 'Helper',
};

/** Roles selectable when adding/editing a non-head profile member. */
export const UHM_EDITABLE_ROLE_OPTIONS: ReadonlyArray<{
  value: UhmRole;
  label: string;
}> = [
  { value: 'member', label: UHM_ROLE_LABELS.member },
  { value: 'helper', label: UHM_ROLE_LABELS.helper },
  { value: 'co_hoh', label: UHM_ROLE_LABELS.co_hoh },
  { value: 'spouse', label: UHM_ROLE_LABELS.spouse },
];

export function uhmRoleLabel(role: UhmRole | null | undefined): string {
  if (!role) {
    return UHM_ROLE_LABELS.member;
  }
  return UHM_ROLE_LABELS[role] ?? UHM_ROLE_LABELS.member;
}

export function getProfileMemberFullName(member: UHMember): string {
  const parts = [
    member.first_name,
    member.middle_name,
    member.last_name,
  ].filter(Boolean);
  return parts.join(' ').trim() || '—';
}

/**
 * Resolves "the profile household" for the forced composition prompt.
 *
 * The backend does not (yet) expose `source` on my-household, so we
 * discriminate structurally: among the unconfirmed profile households we pick
 * the first that is NOT already backed by a certification. Cert-linked
 * households are owned by the cert composition modal, so excluding them
 * prevents a double prompt. We deliberately never fall back to
 * `userHouseholds[0]`.
 */
export function resolveProfileHousehold(
  unconfirmedProfileHouseholds: UserHouseholdData[],
  certifications: Certification[]
): UserHouseholdData | null {
  const certLinkedUserHouseholdIds = new Set<number>();
  for (const cert of certifications ?? []) {
    const uhId = cert.cert_household?.user_household_id;
    if (uhId != null) {
      certLinkedUserHouseholdIds.add(uhId);
    }
  }
  return (
    unconfirmedProfileHouseholds.find(
      (uh) => !certLinkedUserHouseholdIds.has(uh.user_household_id)
    ) ?? null
  );
}
